<template>
    <div class="border-stroke-1 relative flex min-h-0 flex-1 flex-col border-t">
        <div class="flex min-h-0 flex-1 items-start overflow-x-hidden overflow-y-auto">
            <div class="grid min-h-full shrink-0" :class="isSoloGame ? 'w-full' : 'w-1/2'">
                <component
                    :is="playerComponent"
                    :index="0"
                    :player="competitor1"
                    :team="competitor1"
                    :is-bot-turn="isBotTurn"
                />
            </div>
            <template v-if="!isSoloGame">
                <div class="bg-stroke-1 relative h-full w-px shrink-0"></div>
                <div class="grid min-h-full w-1/2 shrink-0">
                    <component
                        :is="playerComponent"
                        :index="1"
                        :player="competitor2"
                        :team="competitor2"
                        :is-bot-turn="isBotTurn"
                    />
                </div>
            </template>
        </div>

        <div ref="gridContainer" class="border-stroke-1 bg-bg flex shrink-0 flex-col border-t">
            <DartScoreGrid
                v-if="gridMode === 'dart'"
                ref="dartGrid"
                :current-player="currentPlayer"
                :disabled="inputDisabled"
                :disable-grid-toggle="requiresDartGridForCheckIn"
                @toggle-grid="toggleGrid"
            />

            <TotalScoreGrid
                v-if="gridMode === 'total'"
                ref="totalGrid"
                :current-player="currentPlayer"
                :disabled="inputDisabled"
                :disable-grid-toggle="requiresDartGridForCheckIn"
                @toggle-grid="toggleGrid"
            />
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth.js'
import { useDartScoring } from '@/composables/useDartScoring.js'
import PlayPlayer from '@/components/PlayPlayer.vue'
import PlayTeam from '@/components/PlayTeam.vue'
import DartScoreGrid from '@/components/ui/DartScoreGrid.vue'
import TotalScoreGrid from '@/components/ui/TotalScoreGrid.vue'

export default {
    name: 'X01Game',
    components: { PlayPlayer, PlayTeam, DartScoreGrid, TotalScoreGrid },
    emits: ['quit-match'],
    setup() {
        const matchStore = useMatchStore()
        const currentPlayerRef = computed(() => matchStore.getCurrentPlayer)
        const { isFirstTurnInLeg, requiresDoubleIn, requiresDoubleOrTripleIn } =
            useDartScoring(currentPlayerRef)

        return { isFirstTurnInLeg, requiresDoubleIn, requiresDoubleOrTripleIn }
    },
    data() {
        return {
            // The user's own choice, persisted - kept separate from `gridMode`
            // (the actually-rendered mode), which can temporarily override it.
            preferredGridMode: localStorage.getItem('dartshopper-grid-mode') || 'total',
            previousPlayer: null,
        }
    },
    computed: {
        matchStore() {
            return useMatchStore()
        },
        authStore() {
            return useAuthStore()
        },
        currentPlayer() {
            return this.matchStore.getCurrentPlayer
        },
        isTeamPlay() {
            return this.matchStore.isTeamPlay
        },
        playerComponent() {
            return this.isTeamPlay ? 'PlayTeam' : 'PlayPlayer'
        },
        profiles() {
            return this.matchStore.getProfiles
        },
        teams() {
            return this.matchStore.getTeams
        },
        isSoloGame() {
            return this.profiles.length === 1 && !this.isTeamPlay
        },
        competitor1() {
            if (this.isTeamPlay) {
                return this.teams[0] || null
            }
            return this.profiles[0] || null
        },
        competitor2() {
            if (this.isTeamPlay) {
                return this.teams[1] || null
            }
            return this.profiles[1] || null
        },
        matchData() {
            return this.matchStore.getMatchData
        },
        competitor1SetsWon() {
            return this.matchStore.getCompetitorSetsWon(this.competitor1?.team)
        },
        competitor2SetsWon() {
            return this.matchStore.getCompetitorSetsWon(this.competitor2?.team)
        },
        competitor1LegsWon() {
            return this.matchStore.getCompetitorLegsWon(this.competitor1?.team)
        },
        competitor2LegsWon() {
            return this.matchStore.getCompetitorLegsWon(this.competitor2?.team)
        },
        isBotTurn() {
            return this.matchStore.isBotTurn
        },
        isCurrentUsersTurn() {
            return (
                this.currentPlayer &&
                String(this.currentPlayer.id) === String(this.authStore.profile?.id)
            )
        },
        inputDisabled() {
            if (this.isBotTurn && !this.matchStore.editingTurn) {
                return true
            }

            // In remote play, only allow input on your own turn.
            return this.matchStore.isRemotePlay && !this.isCurrentUsersTurn
        },
        /**
         * Double-in/master-in can only be verified from the exact dart type
         * thrown (a total score can't tell whether the checking-in dart was
         * actually a double/triple), so the dart-by-dart grid is mandatory for
         * whoever's first scoring turn of the leg it currently is under those
         * check-in settings.
         */
        requiresDartGridForCheckIn() {
            return (
                this.isFirstTurnInLeg && (this.requiresDoubleIn || this.requiresDoubleOrTripleIn)
            )
        },
        /** The actually-rendered mode: forced to 'dart' during a required check-in turn, the user's own preference otherwise. */
        gridMode() {
            return this.requiresDartGridForCheckIn ? 'dart' : this.preferredGridMode
        },
    },
    watch: {
        currentPlayer(newPlayer) {
            // Track the current player (for future use if needed)
            if (newPlayer && !this.previousPlayer) {
                this.previousPlayer = newPlayer
            }
        },
    },
    methods: {
        toggleGrid() {
            // Belt-and-braces: the toggle button is already disabled in this
            // state, but don't let the switch happen even if triggered another way.
            if (this.requiresDartGridForCheckIn) return

            this.preferredGridMode = this.preferredGridMode === 'dart' ? 'total' : 'dart'
            localStorage.setItem('dartshopper-grid-mode', this.preferredGridMode)

            if (this.preferredGridMode === 'dart' && this.$refs.dartGrid) {
                this.$refs.dartGrid.resetDarts()
            } else if (this.preferredGridMode === 'total' && this.$refs.totalGrid) {
                this.$refs.totalGrid.resetScore()
            }
        },
    },
}
</script>
